import {
  DropDownButton,
  type DropDownButtonProps,
} from '@progress/kendo-react-buttons'
import React from 'react'
import {
  type Control,
  useFieldArray,
  type UseFieldArrayAppend,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import { Box } from '~/components/elements/Box'
import { OperatorContainer } from '~/components/elements/OperatorContainer'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'

import { type FilterItem, type InputValues } from '../utils'

import styles from './FilterGroupInput.css'
import { FilterInput, type FilterInputProps } from './FilterInput'

export type FilterGroupInputProps<
  TFilterItem extends FilterItem<TCategory>,
  TCategory extends string,
> = {
  addButtonItems: {
    label: string
    value: TCategory
  }[]
  categoryToFilter: (category: TCategory) => Parameters<
    UseFieldArrayAppend<
      InputValues<TFilterItem, TCategory>,
      // @ts-expect-error
      `filterGroups.${number}`
    >
  >[0]
  control: Control<InputValues<TFilterItem, TCategory>>
  filterGroupIndex: number
  onDelete: (filterGroupIndex: number) => void
  renderInput: FilterInputProps<TFilterItem, TCategory>['renderInput']
}

export const FilterGroupInput = <
  TFilterItem extends FilterItem<TCategory>,
  TCategory extends string,
>({
  addButtonItems,
  categoryToFilter,
  control,
  filterGroupIndex,
  onDelete,
  renderInput,
}: FilterGroupInputProps<TFilterItem, TCategory>) => {
  const { t } = useTranslation()
  const handleDeleteButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    onDelete(filterGroupIndex)
  }, [filterGroupIndex, onDelete])

  const { append, fields, remove } = useFieldArray<
    InputValues<TFilterItem, TCategory>,
    // @ts-expect-error
    `filterGroups.${number}`,
    'fieldId'
  >({
    control,
    keyName: 'fieldId',
    name: `filterGroups.${filterGroupIndex}`,
  })

  const handleAddButtonItemClick = React.useCallback<
    EH<DropDownButtonProps, 'onItemClick'>
  >(
    (event) => {
      const category = event.item.value as TCategory
      const filter = categoryToFilter(category)
      append(filter)
    },
    [append, categoryToFilter],
  )

  return (
    <VStack className={styles.root} padding={2} spacing={4}>
      <StyledButton
        buttonStyle="light"
        className={styles.deleteButton}
        fillMode="flat"
        icon="x"
        onClick={handleDeleteButtonClick}
      >
        {t('delete_definition_1')}
      </StyledButton>

      {match(fields.length)
        .with(0, () => (
          <Typography className={styles.noFiltersMessage} variant="normal">
            {t('no_definitions_specified')}
          </Typography>
        ))
        .otherwise(() => (
          <VStack spacing={2}>
            {fields.map((field, filterIndex) => (
              <OperatorContainer
                key={field.fieldId}
                operator={filterIndex !== 0 ? 'AND' : null}
                size={80}
              >
                <FilterInput
                  control={control}
                  filterGroupIndex={filterGroupIndex}
                  filterIndex={filterIndex}
                  renderInput={renderInput}
                  onDelete={remove}
                />
              </OperatorContainer>
            ))}
          </VStack>
        ))}

      <DropDownButton
        className={styles.addButton}
        icon="plus-outline"
        items={addButtonItems}
        text={t('add_and_definition')}
        textField="label"
        onItemClick={handleAddButtonItemClick}
      />

      <Box />
    </VStack>
  )
}
